/* @jsxImportSource react */

import type { ReactNode } from 'react';
import type { FragmentType } from '@packages/gql/generated/shopping';
import type { DimensionFragmentFragmentDoc } from '@packages/gql/generated/shopping/DimensionFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { ColorTilesDimension } from './ColorTilesDimension/ColorTilesDimension';
import { TilesDimension } from './TilesDimension/TilesDimension';
import { SelectDimension } from './SelectDimension/SelectDimension.client';
import type { VariationSelectionOnSelectHandler } from './types';

/* GraphQL */ `
  fragment DimensionFragment on ProductDimension {
    axisType
    displayName
    type
    ...TilesValuesFragment
    ...ColorTilesValuesFragment
    ...SelectValuesFragment
  }
`;

export type VariationSelectProps = VariationSelectionOnSelectHandler & {
  /**
   * The GraphQL dimension data for the given product
   */
  dimensionData: FragmentType<typeof DimensionFragmentFragmentDoc>;
  sampleOrderLink: ReactNode;
  /**
   * can be removed after abtest
   */
  isTestVariant?: boolean;
};

/**
 * VariationSelect component decides which component-type to be rendered:
 * There are multiple ways, a product and its variations can be selected (e.g. Select, Tiles, ...).
 * Depending on configuration and backend-data, this component chooses a suiting
 * display-type.
 * */
export const VariationSelect = ({
  dimensionData,
  sampleOrderLink,
  onSelect,
  isTestVariant = true,
}: VariationSelectProps) => {
  const dimension = unmask<typeof DimensionFragmentFragmentDoc>(dimensionData);

  let content = <SelectDimension data={dimension} onSelect={onSelect} />;

  if (dimension.type === 'TILES' || dimension.type === 'PRICE_TILES') {
    if (dimension.axisType === 'COLOR') {
      content = <ColorTilesDimension data={dimension} onSelect={onSelect} />;
    } else {
      content = (
        <TilesDimension data={dimension} onSelect={onSelect} isTestVariant={isTestVariant} />
      );
    }
  }

  return (
    <div>
      <p>{dimension.displayName}</p>
      <div className="mt-1">{content}</div>
      {sampleOrderLink !== null && dimension.axisType === 'COLOR' && sampleOrderLink}
    </div>
  );
};
